/* THE MOCKUP — the deliverable back on the building, as a file.

   On-site is the view that sells the idea: the render sitting on a real
   photograph of a real wall. It exists only as DOM, though — an <img> with the
   deliverable's canvas laid over it under a CSS matrix3d — so there are no
   pixels to hand anyone. This makes them.

   WHY A SHADER AND NOT drawImage. The mapping from the deliverable to the
   photograph is projective: a rectangle becomes an arbitrary quadrilateral, and
   parallel edges stop being parallel. Canvas 2D transforms are affine and
   cannot express that; approximating it with subdivided triangles shows its
   seams exactly where the eye is drawn. So the warp is one fragment pass that
   inverts the homography per pixel, which is exact.

   WHY NOT RENDER THE SCENE FROM THE PHOTOGRAPH'S OWN CAMERA. The solve knows
   where the photographer stood, so it is tempting. But the anamorphic
   deliverable IS the thing that will be on that screen, and warping it is what
   guarantees the mockup shows what the screen will show — including where the
   content is cut off by the screen's own edge. Rendering the scene again would
   be a second answer to a question that already has one.

   The homographies are built here rather than borrowed from ui/site-view.js's,
   which follow the preview: its overscan reaches past the screen so off-frame
   objects can be grabbed, and its pan and zoom are navigation. A mockup shows
   the deliverable, whole, and nothing else. */
import * as THREE from 'three';
import { S } from '../state/store.js';
import { homography } from '../core/linalg.js';
import { leftPanelDesc, rightPanelDesc } from '../scene/panels.js';
import { setBezelPass } from '../scene/helpers.js';
import { wholeWallSize } from '../export/multipanel.js';
import { exportName } from './filename.js';
import { cornerCam, flatCam } from '../scene/context.js';
import { beginFrames, renderPanel } from './frame.js';
import { frameCount, frameDur, settleAt } from '../state/timeline.js';
import { createBackdrop, siteClipReady, siteFrameAt } from './site-clip.js';
import { resolveWatermark, stampWatermark } from './png.js';
import { EXPORT_COST } from '../services/limits.js';
import { exportMP4 } from './mp4.js';

import { track } from '../services/analytics.js';
import { toast } from '../ui/toast.js';

/** The output size: the photo's shape, its long side at 1920, both sides even.
 *  Even because H.264 refuses odd dimensions, and the still and the video have
 *  to be the same picture at the same size. */
export function onsiteSize(){
  const p = S.photo;
  if(!p || !p.w || !p.h) return { w: 1920, h: 1080 };
  const LONG = 1920;
  const w = p.w >= p.h ? LONG : Math.round(LONG * (p.w / p.h));
  const h = p.w >= p.h ? Math.round(LONG * (p.h / p.w)) : LONG;
  return { w: w - (w & 1), h: h - (h & 1) };
}

/* Each panel, and where its four corners land on the photograph. Normalised
   marks, so they scale to whatever size the output is. The second panel's
   first two corners ARE the first panel's right edge — one copy of those
   points, which is why the two can never come apart. */
function panels(){
  /* A SCREEN ARRAY needs no arm of its own here: leftPanelDesc() IS the wall
     (S.screen is the wall — see store.js), S.cornerPanel is null, and the four
     marks are the wall's own corners. The only thing that differs is the size
     it is rendered at: an array's S.res is the whole grid at tile pitch, 5823 px
     across for a 3 x 2 of 1080p panels, and a mockup does not need — and a
     browser does not enjoy — rendering that to be squeezed into a 1920 px
     photograph. The wall's review size is the right one, and it is the same
     picture the whole-wall export produces. */
  const wall = S.screenArray ? wholeWallSize() : null;
  const out = [{ desc: leftPanelDesc(), cam: flatCam, resW: wall ? wall.w : S.res.w,
                 resH: wall ? wall.h : S.res.h,
                 marks: [S.corners.tl, S.corners.tr, S.corners.br, S.corners.bl] }];
  const r = rightPanelDesc();
  if(r && S.cornerPanel)
    out.push({ desc: r, cam: cornerCam, resW: S.cornerPanel.resW,
               marks: [S.corners.tr, S.cornerPanel.outer.tr,
                       S.cornerPanel.outer.br, S.corners.br] });
  return out;
}

const VERT = `attribute vec2 p; void main(){ gl_Position = vec4(p, 0.0, 1.0); }`;
/* Inverse-mapped: for each output pixel, where did it come from in the
   deliverable? Outside it, nothing is drawn — which is what makes the render
   stop exactly at the screen's edge rather than at a rectangle around it. */
const FRAG = `
precision highp float;
uniform sampler2D src;
uniform mat3 Hinv;          // output pixels -> deliverable pixels
uniform vec2 srcSize;
uniform vec2 outSize;
void main(){
  vec3 q = Hinv * vec3(gl_FragCoord.x, outSize.y - gl_FragCoord.y, 1.0);
  vec2 uv = q.xy / q.z;
  if(uv.x < 0.0 || uv.y < 0.0 || uv.x > srcSize.x || uv.y > srcSize.y) discard;
  /* No flip here. gl_FragCoord is measured from the bottom and the homography
     works in top-down pixels, which is what the line above corrects for — but
     texImage2D from a canvas puts the canvas's FIRST row at v=0, so the source
     is already top-down too. Flipping again put the render on the building
     upside down. */
  gl_FragColor = texture2D(src, uv / srcSize);
}`;

/* Exported for the Unwrap tool (unwrap/warp.js), which runs the same inverse-mapped
   pass the other way round: the same shader, a different matrix. */
export function compile(gl){
  const sh = (t, s) => { const o = gl.createShader(t); gl.shaderSource(o, s);
    gl.compileShader(o);
    if(!gl.getShaderParameter(o, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(o));
    return o; };
  const p = gl.createProgram();
  gl.attachShader(p, sh(gl.VERTEX_SHADER, VERT));
  gl.attachShader(p, sh(gl.FRAGMENT_SHADER, FRAG));
  gl.linkProgram(p);
  if(!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(p));
  const buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 3,-1, -1,3]), gl.STATIC_DRAW);
  const loc = gl.getAttribLocation(p, 'p');
  gl.enableVertexAttribArray(loc);
  gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
  return p;
}

/**
 * One frame of the mockup: the photograph with the current deliverable warped
 * onto the screen. The scene must already be at the instant wanted — the caller
 * owns settling video, exactly as the other exporters do.
 *
 * @param {HTMLImageElement} photo  the decoded reference photograph
 * @param {HTMLCanvasElement} [into] reused across frames of a video
 */
/* WHERE EACH PANEL ENDS UP WHEN IT IS PINNED FLAT.
   Laid out in a row in the order panels() gives them, which is at most two —
   a wall composites as ONE quad (panels() says so: leftPanelDesc IS the wall),
   so this only ever spreads a corner screen's two panels, and their shared
   edge stays shared because their cells abut.

   FITTED, not stretched to the frame's own corners. The animation ends on the
   file that goes on the screen, and a deliverable shown at the wrong aspect at
   the very moment it is being handed over would be worse than no reveal. */
export function flatCells(list, w, h, margin = 0, gap = 0){
  const n = list.length;
  const ws = list.map(p => p.resW);
  const hs = list.map(p => p.resH ?? S.res.h);
  const totalW = ws.reduce((a, b) => a + b, 0);
  const maxH = Math.max(...hs);
  /* A MARGIN, and a GAP between two screens — both in output pixels, both optional.
     The margin keeps the flat file off the frame's edge, so the resolve ends on the
     deliverable with room around it instead of filling the picture. It can never
     take more than 45% of the short side (there would be nothing left to show).
     The gap is only between screens, so a single one ignores it, and it can never
     take more than 30% of the width left once the margin is out.
     Both default to 0, which is the layout before there was a choice. */
  const m = Math.min(Math.max(0, +margin || 0), Math.min(w, h) * 0.45);
  const g = n > 1 ? Math.min(Math.max(0, +gap || 0), (w - 2 * m) * 0.3 / (n - 1)) : 0;
  const k = Math.min((w - 2 * m - g * (n - 1)) / totalW, (h - 2 * m) / maxH);
  let x = (w - (totalW * k + g * (n - 1))) / 2;
  const y = (h - maxH * k) / 2;
  return list.map((p, i) => {
    const pw = ws[i] * k, ph = hs[i] * k;
    const cell = [[x, y], [x + pw, y], [x + pw, y + ph], [x, y + ph]];
    x += pw + g;
    return cell;
  });
}

/* The margin and the gap are asked for as a percentage of the frame's SHORTER
   side, so the same number looks the same on a landscape and a portrait photo,
   and a margin of 5 is as deep at the top as it is at the sides. */
export const FLAT_MARGIN_MAX = 30;
export const FLAT_GAP_MAX = 15;
const pctOf = (v, max) => Math.min(max, Math.max(0, Number.isFinite(+v) ? +v : 0));
export const clampFlatMargin = v => pctOf(v, FLAT_MARGIN_MAX);
export const clampFlatGap = v => pctOf(v, FLAT_GAP_MAX);

/** How many separate screens the flat layout has: two for a corner screen, else one.
 *  A video wall composites as ONE quad (see panels()), so it is one here too. */
export function onsitePanelCount(){
  try { return panels().length; } catch (_) { return 1; }
}

/** Where the flat layout lands in the output frame, for the settings' small preview. */
export function flatPreview(marginPct, gapPct){
  try {
    const { w, h } = onsiteSize();
    const short = Math.min(w, h);
    return { w, h, cells: flatCells(panels(), w, h,
      short * clampFlatMargin(marginPct) / 100, short * clampFlatGap(gapPct) / 100) };
  } catch (_) { return null; }
}
/* Smoothstep. The pin starts and ends at rest, so the picture settles onto the
   deliverable rather than arriving at full speed and stopping dead. */
const ease = t => t * t * (3 - 2 * t);

export function compositeOnSite(photo, into, pin = 0, layout = null){
  const { w, h } = onsiteSize();
  const out = into || document.createElement('canvas');
  out.width = w; out.height = h;
  const c2 = out.getContext('2d');
  c2.clearRect(0, 0, w, h);
  const e = Math.max(0, Math.min(1, pin)) ? ease(Math.max(0, Math.min(1, pin))) : 0;
  /* BLACK UNDERNEATH, always. The photograph covers the whole canvas at pin 0,
     so this is invisible there and the still export is unchanged — but as the
     photo fades it has to fade TO something. Left transparent, the last frames
     of the resolve are undefined alpha in a format that has none, and what the
     encoder makes of them is its business rather than a decision anyone took.
     Black is what a deliverable sits on. */
  if(e > 0){ c2.fillStyle = '#000'; c2.fillRect(0, 0, w, h); }
  /* The building goes as the render squares up — by the end there is nothing
     but the deliverable, which is the whole point of resolving to flat. */
  c2.globalAlpha = 1 - e;
  c2.drawImage(photo, 0, 0, w, h);
  c2.globalAlpha = 1;

  const warp = document.createElement('canvas');
  warp.width = w; warp.height = h;
  const gl = warp.getContext('webgl', { alpha: true, premultipliedAlpha: false });
  if(!gl) return out;                       // no GL: the photo alone, not a crash
  const prog = compile(gl);
  gl.useProgram(prog);
  gl.viewport(0, 0, w, h);
  gl.clearColor(0, 0, 0, 0);
  gl.clear(gl.COLOR_BUFFER_BIT);
  gl.enable(gl.BLEND);
  gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

  const list = panels();
  const short = Math.min(w, h);
  const cells = e > 0
    ? flatCells(list, w, h, short * clampFlatMargin(layout?.margin) / 100, short * clampFlatGap(layout?.gap) / 100)
    : null;
  /* The mockup's margins between a wall's screens show the building when
     "margins show the photo" is on — set HERE, for these renders only, rather
     than inherited from whichever view drew last (Split's last pane is Orbit or
     Flat, which draws them black). Put back to the deliverable's black after,
     which is what beginFrames set for the rest of the export. */
  setBezelPass(true);
  try {
  for(let pi = 0; pi < list.length; pi++){
    const p = list[pi];
    /* Rendered at the deliverable's own resolution — this is the picture that
       will be on the screen, not a preview of it. */
    const src = renderPanel(p.desc, null, p.resW, p.resH ?? S.res.h, p.cam);
    const marked = p.marks.map(m => [m.x * w, m.y * h]);
    /* THE CORNER PIN. Each corner travels from where it was marked on the
       photograph to where that panel's own corner belongs once it is flat.
       Four interpolated points still describe a homography, so the warp pass
       below needs no idea any of this is happening — which is why the reveal
       costs a lerp rather than a second rendering path. */
    const dst = cells
      ? marked.map((q, i) => [q[0] + (cells[pi][i][0] - q[0]) * e,
                              q[1] + (cells[pi][i][1] - q[1]) * e])
      : marked;
    const Hinv = homography(dst, [[0,0], [src.width,0], [src.width,src.height], [0,src.height]]);
    if(!Hinv) continue;                     // degenerate marks — skip, do not throw

    const tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, src);
    gl.uniform1i(gl.getUniformLocation(prog, 'src'), 0);
    // column-major for WebGL, and the homography is stored row-major
    gl.uniformMatrix3fv(gl.getUniformLocation(prog, 'Hinv'), false, new Float32Array([
      Hinv[0], Hinv[3], Hinv[6],
      Hinv[1], Hinv[4], Hinv[7],
      Hinv[2], Hinv[5], Hinv[8]
    ]));
    gl.uniform2f(gl.getUniformLocation(prog, 'srcSize'), src.width, src.height);
    gl.uniform2f(gl.getUniformLocation(prog, 'outSize'), w, h);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    gl.deleteTexture(tex);
  }
  } finally { setBezelPass(false); }
  c2.drawImage(warp, 0, 0);
  return out;
}

/** The photograph itself, decoded and ready to draw. */
function photoEl(){
  const el = document.getElementById('siteImg');
  return el && el.complete && el.naturalWidth ? el : null;
}

/** A still of the mockup. */
export async function exportOnSitePNG(opts = {}){
  /* A filmed video behind it instead of the photograph, when one is loaded and asked for: the still is
     the frame of it at the playhead's moment (the video loops under the animation). */
  const filmed = !!opts.filmed && siteClipReady();
  const photo = filmed ? null : photoEl();
  if(!photo && !filmed){ toast('Load a reference photo first.', true); return; }
  const { w, h } = onsiteSize();
  const pass = beginFrames();
  /* Waited for, not assumed — the same reason export/png.js gives: a video
     object's frame comes from a decoder, and a mockup showing whatever was on
     its canvas from the last repaint is wrong in a way nobody would catch. */
  let out;
  try {
    await settleAt(S.playhead);
    out = compositeOnSite(filmed ? await siteFrameAt(S.playhead) : photo);
  } finally { pass.end(); }

  const watermark = await resolveWatermark(opts.watermark, EXPORT_COST.still);
  if(watermark) await stampWatermark(out);

  out.toBlob(b => {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(b);
    a.download = exportName({ kind: 'Onsite', spec: `${w}x${h}`, ext: 'png' });
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 4000);
    toast(`Exported the on-site mockup, ${w} × ${h}.`);
    track('export_onsite_png', { watermarked: watermark, w, h, filmed });
  }, 'image/png');
}

/** The mockup over time. The encoder, the size ceiling, cancellation, progress
 *  and the one credit are all export/mp4.js's — this supplies only the picture,
 *  so the two exports cannot drift apart on any of the rest. */
/* How long the resolve takes. Long enough to read as a move rather than a cut,
   short enough that nobody waits through it twice. */
export const RESOLVE_SECONDS = 1.6;

/**
 * How far through the corner pin an instant is: 0 on the building, 1 flat.
 *
 * A pure function of the clock so the schedule can be checked without encoding
 * a video — the alternative is a number computed inside a paint callback,
 * which can only be verified by watching the result and trusting your eyes.
 *
 * @param t        seconds into the clip
 * @param dur      the timeline's length
 * @param resolve  how long the move takes; 0 disables it entirely
 * @param startAt  when it begins; the midpoint unless told otherwise, which is
 *                 where it was fixed before this was a choice
 */
export function pinAt(t, dur, resolve, startAt, stopAt){
  if(!(resolve > 0)) return 0;
  const from = startAt == null ? Math.max(0.1, dur) / 2 : startAt;
  const up = Math.max(0, Math.min(1, (t - from) / resolve));
  if(stopAt == null) return up;                 // out, and stays out
  /* RISING MINUS FALLING. The return is the same move read backwards, so it is
     the same ramp started later and subtracted — which gives the hold between
     them for free (up is 1, down still 0) and cannot drift out of step with
     the way out the way two separate schedules would. */
  const down = Math.max(0, Math.min(1, (t - stopAt) / resolve));
  return Math.max(0, up - down);
}

/**
 * When the pin may begin: never before the clip, never so late that it is
 * still moving as the video ends. Landing flat is the point of it — a resolve
 * cut off mid-move delivers neither the mockup nor the file.
 */
export const clampResolveAt = (at, dur, resolve) =>
  Math.min(Math.max(0, at), Math.max(0, dur - resolve));

/**
 * And when it may start back. Never before it has arrived — a return that
 * begins mid-flight never shows the flat file at all, which is the one thing
 * the move exists to show — and never so late that it is still on its way home
 * as the clip ends.
 *
 * @returns the clamped time, or null when the clip has no room for a return
 */
export function clampResolveStop(at, dur, resolve, startAt){
  const earliest = startAt + resolve;
  const latest = dur - resolve;
  if(latest < earliest) return null;            // nowhere to put it
  return Math.min(Math.max(earliest, at), latest);
}

export async function exportOnSiteMP4(opts = {}){
  /* With a filmed video behind it every frame of the clip is a background, so the render is made over
     the building as it moves. The video loops when the animation is longer than it. */
  const filmed = !!opts.filmed && siteClipReady();
  const photo = filmed ? null : photoEl();
  if(!photo && !filmed) throw new Error('Load a reference photo first.');
  const { w, h } = onsiteSize();
  /* HALF AND HALF, inside the clip's own length. The first half is the mockup
     on the building; at the midpoint the render un-warps to the deliverable
     and the clip finishes there. No extra frames and no change to how long the
     file is — which is why export/mp4.js needs to know nothing about any of
     this and still renders exactly frameCount() frames.

     The resolve is shortened on a clip too short to hold it, so a two-second
     timeline still gets a move rather than a cut. */
  const dur = Math.max(0.1, S.timeline.duration);
  const resolve = opts.resolveToFlat ? Math.min(RESOLVE_SECONDS, dur / 2) : 0;
  /* Where it starts is the caller's, clamped so the move always finishes
     inside the clip. Unset means the midpoint — the moment it was fixed at
     before this became a choice, so an export nobody has touched is the same
     file it was. */
  const startAt = clampResolveAt(opts.resolveAt == null ? dur / 2 : opts.resolveAt, dur, resolve);
  /* And whether it comes back. Unset means it does not — the clip ends on the
     deliverable, which is what this did before there was a choice. */
  const stopAt = (resolve > 0 && opts.stopsAt != null)
    ? clampResolveStop(opts.stopsAt, dur, resolve, startAt) : null;
  // How much room the flat file keeps around it, and between two screens. Percentages; unset is none.
  const layout = { margin: clampFlatMargin(opts.flatMargin), gap: clampFlatGap(opts.flatGap) };
  /* Every instant, in order, before the first frame: the video's batch decoder needs the list up front. */
  const backdrop = filmed
    ? createBackdrop(Array.from({ length: frameCount() }, (_, f) => f * frameDur()))
    : null;
  try {
    return await exportMP4({
      ...opts,
      size: { w, h },
      /* After the spread, not before: the price is this function's to state,
         not something a caller can talk it out of. */
      cost: EXPORT_COST.onsiteVideo,
      kind: (filmed ? 'Onsite_Filmed' : 'Onsite') + (opts.resolveToFlat ? '_To_Flat' : ''),
      paint: async (canvas, t) => {
        const pin = pinAt(t, dur, resolve, startAt, stopAt);
        const pass = beginFrames();
        try {
          await settleAt(t);
          const cv = compositeOnSite(backdrop ? await backdrop.next() : photo, undefined, pin, layout);
          if(canvas.width !== cv.width || canvas.height !== cv.height){
            canvas.width = cv.width; canvas.height = cv.height;
          }
          const c2 = canvas.getContext('2d');
          c2.clearRect(0, 0, canvas.width, canvas.height);
          c2.drawImage(cv, 0, 0);
        } finally { pass.end(); }
      }
    });
  } finally { await backdrop?.close(); }
}
